/**
 * Simple router for handling dynamic routes like /appname/privacy
 */
export interface Route {
  pattern: RegExp;
  handler: (params: Record<string, string>) => void;
}

export interface RouteParams {
  appName: string;
  page: string;
  [key: string]: string;
}

class Router {
  private routes: Route[] = [];
  private currentRoute: string = '';

  constructor() {
    this.init();
  }

  private init(): void {
    // Listen for popstate events (back/forward navigation)
    window.addEventListener('popstate', () => {
      this.handleRouteChange();
    });

    // Handle initial page load
    this.handleRouteChange();
  }

  /**
   * Add a route pattern and its handler
   */
  addRoute(pattern: string, handler: (params: RouteParams) => void): void {
    // Convert pattern like "/app/:appName/:page" to regex
    const regexPattern = pattern
      .replace(/:[^\s/]+/g, '([^/]+)')
      .replace(/\//g, '\\/');
    
    const regex = new RegExp(`^${regexPattern}$`);
    
    this.routes.push({
      pattern: regex,
      handler: (params) => handler(params as RouteParams)
    });
  }

  /**
   * Navigate to a new route
   */
  navigate(path: string): void {
    window.history.pushState(null, '', path);
    this.handleRouteChange();
  }

  /**
   * Handle route changes
   */
  private handleRouteChange(): void {
    const path = window.location.pathname;
    this.currentRoute = path;

    // Try to match against registered routes
    for (const route of this.routes) {
      const match = path.match(route.pattern);
      if (match) {
        // Extract parameters from the match
        const params = this.extractParams(path);
        route.handler(params);
        return;
      }
    }

    // No route matched - handle 404
    this.handle404();
  }

  /**
   * Extract parameters from the current path
   */
  private extractParams(path: string): Record<string, string> {
    const parts = path.split('/').filter(Boolean);
    
    // For routes like /appname/privacy
    if (parts.length >= 2 && parts[0] && parts[1]) {
      return {
        appName: parts[0],
        page: parts[1]
      };
    }

    return {};
  }

  /**
   * Handle 404 errors
   */
  private handle404(): void {
    // If it's a dynamic route that doesn't exist, redirect to home
    const path = window.location.pathname;
    if (path !== '/' && !path.startsWith('/assets/')) {
      // eslint-disable-next-line no-console
      console.warn(`Route not found: ${path}`);
      // You can choose to redirect to home or show a 404 page
      // this.navigate('/');
    }
  }

  /**
   * Get current route
   */
  getCurrentRoute(): string {
    return this.currentRoute;
  }
}

export default Router;